import { extendTheme } from '@chakra-ui/react';

// River City Fights color scheme based on their branding
const theme = extendTheme({
  colors: {
    brand: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444', // Primary red
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
    },
    dark: {
      50: '#f7fafc',
      100: '#edf2f7',
      200: '#e2e8f0',
      300: '#cbd5e0',
      400: '#a0aec0',
      500: '#718096',
      600: '#4a5568',
      700: '#2d3748',
      800: '#1a202c', // Primary dark
      900: '#171923',
    },
    accent: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
    }
  },
  fonts: {
    heading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  styles: {
    global: {
      body: {
        bg: 'dark.900',
        color: 'white',
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: 'bold',
        borderRadius: 'md',
      },
      variants: {
        solid: {
          bg: 'brand.500',
          color: 'white',
          _hover: {
            bg: 'brand.600',
            transform: 'translateY(-2px)',
            boxShadow: 'lg',
          },
          _active: {
            bg: 'brand.700',
            transform: 'translateY(0)',
          },
          transition: 'all 0.2s',
        },
        outline: {
          borderColor: 'brand.500',
          color: 'brand.500',
          _hover: {
            bg: 'brand.500',
            color: 'white',
            transform: 'translateY(-2px)',
            boxShadow: 'lg',
          },
          transition: 'all 0.2s',
        },
        ghost: {
          color: 'white',
          _hover: {
            bg: 'whiteAlpha.200',
          },
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: 'dark.800',
          borderRadius: 'lg',
          boxShadow: 'xl',
          border: '1px solid',
          borderColor: 'dark.700',
        },
      },
    },
    Heading: {
      baseStyle: {
        color: 'white',
      },
    },
    Text: {
      baseStyle: {
        color: 'gray.300',
      },
    },
    Input: {
      baseStyle: {
        field: {
          bg: 'dark.700',
          borderColor: 'dark.600',
          color: 'white',
          _placeholder: {
            color: 'gray.400',
          },
          _hover: {
            borderColor: 'dark.500',
          },
          _focus: {
            borderColor: 'blue.500',
            boxShadow: '0 0 0 1px #3182ce',
          },
        },
      },
    },
    Textarea: {
      baseStyle: {
        bg: 'dark.700',
        borderColor: 'dark.600',
        color: 'white',
        _placeholder: {
          color: 'gray.400',
        },
        _hover: {
          borderColor: 'dark.500',
        },
        _focus: {
          borderColor: 'blue.500',
          boxShadow: '0 0 0 1px #3182ce',
        },
      },
    },
    FormLabel: {
      baseStyle: {
        color: 'gray.300',
      },
    },
    Modal: {
      baseStyle: {
        dialog: {
          bg: 'dark.800',
          borderColor: 'dark.700',
        },
        header: {
          color: 'white',
        },
        closeButton: {
          color: 'gray.400',
        },
      },
    },
    Select: {
      baseStyle: {
        field: {
          bg: 'dark.700',
          borderColor: 'dark.600',
          color: 'white',
          _hover: {
            borderColor: 'dark.500',
          },
          _focus: {
            borderColor: 'blue.500',
            boxShadow: '0 0 0 1px #3182ce',
          },
        },
        icon: {
          color: 'gray.400',
        },
      },
      variants: {
        outline: {
          field: {
            bg: 'dark.700',
            borderColor: 'dark.600',
            color: 'white',
            _hover: {
              borderColor: 'dark.500',
            },
            _focus: {
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px #3182ce',
            },
          },
        },
      },
    },
    Menu: {
      baseStyle: {
        list: {
          bg: 'dark.700',
          borderColor: 'dark.600',
        },
        item: {
          bg: 'dark.700',
          color: 'white',
          _hover: {
            bg: 'dark.600',
          },
        },
      },
    },
    Alert: {
      baseStyle: {
        container: {
          borderRadius: 'md',
        },
      },
      variants: {
        solid: (props) => {
          const { status } = props;

          if (status === 'error') {
            return {
              container: {
                bg: 'red.600',
                color: 'white',
                border: '1px solid',
                borderColor: 'red.500',
              },
              icon: {
                color: 'white',
              },
            };
          }

          if (status === 'warning') {
            return {
              container: {
                bg: 'orange.600',
                color: 'white',
                border: '1px solid',
                borderColor: 'orange.500',
              },
              icon: {
                color: 'white',
              },
            };
          }

          if (status === 'success') {
            return {
              container: {
                bg: 'green.600',
                color: 'white',
                border: '1px solid',
                borderColor: 'green.500',
              },
              icon: {
                color: 'white',
              },
            };
          }

          if (status === 'info') {
            return {
              container: {
                bg: 'blue.600',
                color: 'white',
                border: '1px solid',
                borderColor: 'blue.500',
              },
              icon: {
                color: 'white',
              },
            };
          }

          // Default fallback
          return {
            container: {
              bg: 'gray.600',
              color: 'white',
            },
            icon: {
              color: 'white',
            },
          };
        },
        subtle: (props) => {
          const { status } = props;

          if (status === 'error') {
            return {
              container: {
                bg: 'red.900',
                color: 'red.100',
                border: '1px solid',
                borderColor: 'red.700',
              },
              icon: {
                color: 'red.300',
              },
            };
          }

          if (status === 'warning') {
            return {
              container: {
                bg: 'orange.900',
                color: 'orange.100',
                border: '1px solid',
                borderColor: 'orange.700',
              },
              icon: {
                color: 'orange.300',
              },
            };
          }

          if (status === 'success') {
            return {
              container: {
                bg: 'green.900',
                color: 'green.100',
                border: '1px solid',
                borderColor: 'green.700',
              },
              icon: {
                color: 'green.300',
              },
            };
          }

          if (status === 'info') {
            return {
              container: {
                bg: 'blue.900',
                color: 'blue.100',
                border: '1px solid',
                borderColor: 'blue.700',
              },
              icon: {
                color: 'blue.300',
              },
            };
          }

          // Default fallback
          return {
            container: {
              bg: 'gray.800',
              color: 'gray.100',
            },
            icon: {
              color: 'gray.300',
            },
          };
        },
      },
      defaultProps: {
        variant: 'subtle', // Use subtle variant by default for better contrast in dark theme
      },
    },
  },
});

export default theme;
